import React, { useEffect, useState } from "react";
import { theme, card, emptyFrame, floatingSheet, btnPrimary, inputStyle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { FRAMEWORK_LABELS } from "../context/FrameworksContext";
import type { GroupMember } from "./GroupOverview";

// Consultancy view: link a parent organisation and its subsidiaries into a
// group. Every member must be a client of this consultancy and can belong to
// one group only. The parent's owners then see the group overview and can
// open each subsidiary (lib/scope.ts).

interface GroupRow {
  id: string;
  name: string;
  parentOrganizationId: string;
  organizations: Array<{ id: string; name: string; isParent: boolean }>;
  consolidatedHeadcount: number | null;
  consolidatedTurnoverAmount: number | null;
  consolidatedTurnoverCurrency: string | null;
  members?: GroupMember[];
}

interface ClientOption { id: string; name: string }

const CURRENCIES = ["GBP", "EUR", "USD", "CHF", "AUD", "CAD", "SGD", "HKD", "JPY", "NOK", "SEK", "DKK"];

const fieldStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const labelStyle: React.CSSProperties = { fontSize: 11, color: theme.color.subtle, fontWeight: 600 };
const hintStyle: React.CSSProperties = { fontSize: 11, color: theme.color.subtle, lineHeight: 1.4 };
const smallBtn: React.CSSProperties = { padding: "3px 10px", borderRadius: 4, border: `1px solid ${theme.color.border}`, background: "transparent", color: theme.color.text, fontSize: 12, cursor: "pointer" };

function GroupModal({ initial, clients, grouped, onClose, onSaved }: {
  initial?: GroupRow;
  clients: ClientOption[];
  /** organisationId → group name, for every organisation already in some group. */
  grouped: Map<string, string>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName]         = useState(initial?.name ?? "");
  const [parentId, setParentId] = useState(initial?.parentOrganizationId ?? "");
  const [memberIds, setMemberIds] = useState<string[]>(initial ? initial.organizations.filter((o) => !o.isParent).map((o) => o.id) : []);
  const [headcount, setHeadcount] = useState(initial?.consolidatedHeadcount != null ? String(initial.consolidatedHeadcount) : "");
  const [amount, setAmount]     = useState(initial?.consolidatedTurnoverAmount != null ? String(initial.consolidatedTurnoverAmount) : "");
  const [currency, setCurrency] = useState(initial?.consolidatedTurnoverCurrency ?? "GBP");
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");

  // Organisations free to join: not in another group (members of this one stay eligible).
  const eligible = clients.filter((c) => !grouped.has(c.id) || grouped.get(c.id) === initial?.name);

  function toggleMember(id: string) {
    setMemberIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!parentId) { setError("Choose the parent organisation."); return; }
    const members = memberIds.filter((id) => id !== parentId);
    if (members.length === 0) { setError("Add at least one subsidiary."); return; }
    const h = headcount.trim() === "" ? null : Number(headcount);
    const a = amount.trim() === "" ? null : Number(amount);
    if ((h !== null && (!Number.isInteger(h) || h < 0)) || (a !== null && (!Number.isFinite(a) || a < 0))) { setError("Enter whole, non-negative numbers."); return; }
    setSaving(true);
    try {
      const body = {
        name: name.trim(), parentOrganizationId: parentId, organizationIds: members,
        consolidatedHeadcount: h, consolidatedTurnoverAmount: a, consolidatedTurnoverCurrency: a === null ? null : currency,
      };
      if (initial) await api.patch(`/groups/${initial.id}`, body);
      else await api.post("/groups", body);
      onSaved();
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  }

  const overlay: React.CSSProperties = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 };

  return (
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...floatingSheet, padding: 20, width: "100%", maxWidth: 560, maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, paddingBottom: 12, borderBottom: "1px solid var(--c-line)" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading }}>{initial ? "Edit group" : "New group"}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.muted, fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        <form onSubmit={save} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Group name</label>
            <input style={inputStyle} value={name} autoFocus required maxLength={120} onChange={(e) => setName(e.target.value)} placeholder="e.g. Acme Holdings" />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Parent organisation</label>
            <select style={inputStyle} value={parentId} required onChange={(e) => setParentId(e.target.value)}>
              <option value="">Choose…</option>
              {eligible.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div style={hintStyle}>Owners and compliance managers of the parent see the group overview and can open every subsidiary; owners can work in them.</div>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Subsidiaries</label>
            {eligible.filter((c) => c.id !== parentId).length === 0 ? (
              <div style={hintStyle}>No other organisations are free to join — every client is already in a group.</div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {eligible.filter((c) => c.id !== parentId).map((c) => {
                  const sel = memberIds.includes(c.id);
                  return (
                    <button key={c.id} type="button" onClick={() => toggleMember(c.id)} style={{
                      fontSize: 11, padding: "4px 10px", borderRadius: 3, cursor: "pointer", fontWeight: sel ? 600 : 400,
                      border: `1px solid ${sel ? theme.color.accent : theme.color.border}`,
                      background: sel ? theme.color.accentBg : "transparent",
                      color: sel ? theme.color.accentText : theme.color.muted,
                    }}>
                      {c.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div style={{ padding: 12, borderRadius: 3, background: theme.color.panelAlt, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading }}>Consolidated figures (optional)</div>
            <div style={hintStyle}>NIS2, DORA and UK NIS size an organisation by its linked enterprises together. Record the group's headcount and turnover so a small subsidiary of a large group is assessed as large. Data protection duties stay per legal entity.</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 100px", gap: 10 }}>
              <div style={fieldStyle}>
                <label style={labelStyle}>Group headcount</label>
                <input style={inputStyle} value={headcount} inputMode="numeric" onChange={(e) => setHeadcount(e.target.value)} />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Group turnover</label>
                <input style={inputStyle} value={amount} inputMode="decimal" onChange={(e) => setAmount(e.target.value)} />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Currency</label>
                <select style={inputStyle} value={currency} onChange={(e) => setCurrency(e.target.value)}>
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          </div>
          {error && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} style={{ ...inputStyle, width: "auto", padding: "8px 16px", cursor: "pointer" }}>Cancel</button>
            <button type="submit" disabled={saving || !name.trim()} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>{saving ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function ConsultantGroupsTab({ clients, canManage, onOpenClient }: {
  clients: ClientOption[];
  canManage: boolean;
  onOpenClient?: (id: string, name: string) => void;
}) {
  const [groups, setGroups]     = useState<GroupRow[]>([]);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [details, setDetails]   = useState<Map<string, GroupMember[]>>(new Map());
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing]   = useState<GroupRow | null>(null);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [error, setError]       = useState("");

  function load() {
    setLoading(true);
    api.get<{ groups: GroupRow[] }>("/groups")
      .then((d) => setGroups(d.groups))
      .catch((e) => setError(e instanceof Error && e.message ? `Could not load groups: ${e.message}` : "Could not load groups"))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  async function toggleExpand(id: string) {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!details.has(id)) {
      try {
        const d = await api.get<{ group: GroupRow & { members: GroupMember[] } }>(`/groups/${id}`);
        setDetails((prev) => new Map(prev).set(id, d.group.members));
      } catch { /* row stays collapsed-looking */ }
    }
  }

  async function dissolve(id: string) {
    setError("");
    try {
      await api.del(`/groups/${id}`);
      setConfirmDel(null);
      load();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not dissolve the group"));
    }
  }

  const grouped = new Map<string, string>();
  for (const g of groups) for (const o of g.organizations) grouped.set(o.id, g.name);

  const cell: React.CSSProperties = { padding: "12px 16px", borderBottom: `1px solid ${theme.color.border}`, fontSize: 13, color: theme.color.text, verticalAlign: "top" };

  return (
    <div className="page-pad" style={{ fontFamily: theme.font }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>Organisation groups</h1>
          <p style={{ margin: 0, fontSize: 13, color: theme.color.subtle, maxWidth: 680 }}>
            Link a parent organisation with its subsidiaries when each certifies or is regulated separately. Every member keeps its own
            management system; the parent's owners get a group overview and can work in each subsidiary. A group run as one
            management system is better described as sites on a single organisation.
          </p>
        </div>
        {canManage && <button onClick={() => setShowCreate(true)} style={{ ...btnPrimary, flexShrink: 0 }}>+ New group</button>}
      </div>

      {error && <div style={{ fontSize: 12, color: theme.color.dangerText, marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div>
      ) : groups.length === 0 ? (
        <div style={{ ...emptyFrame, color: theme.color.subtle, fontSize: 13 }}>No groups yet.</div>
      ) : (
        <div style={{ ...card, padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {groups.map((g) => {
                const parent = g.organizations.find((o) => o.isParent);
                const subs = g.organizations.filter((o) => !o.isParent);
                const members = details.get(g.id);
                return (
                  <React.Fragment key={g.id}>
                    <tr>
                      <td style={cell}>
                        <div style={{ fontWeight: 600, color: theme.color.heading }}>{g.name}</div>
                        <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>
                          Parent: {parent?.name ?? "—"} · {subs.length} subsidiar{subs.length === 1 ? "y" : "ies"}
                          {g.consolidatedHeadcount != null ? ` · ${g.consolidatedHeadcount.toLocaleString("en-GB")} staff` : ""}
                          {g.consolidatedTurnoverAmount != null ? ` · ${g.consolidatedTurnoverCurrency} ${g.consolidatedTurnoverAmount.toLocaleString("en-GB")}` : ""}
                        </div>
                      </td>
                      <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap" }}>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          <button onClick={() => toggleExpand(g.id)} style={smallBtn}>{expanded === g.id ? "Hide" : "Members"}</button>
                          {canManage && <button onClick={() => setEditing(g)} style={smallBtn}>Edit</button>}
                          {canManage && <button onClick={() => setConfirmDel(g.id)} style={{ ...smallBtn, color: theme.color.danger }}>Dissolve</button>}
                        </div>
                      </td>
                    </tr>
                    {confirmDel === g.id && (
                      <tr>
                        <td colSpan={2} style={{ ...cell, background: theme.color.panelAlt }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 13 }}>Dissolve <strong>{g.name}</strong>? Every organisation carries on alone; nothing else is deleted.</span>
                            <button onClick={() => dissolve(g.id)} style={{ ...smallBtn, background: theme.color.danger, color: "#fff", border: "none", fontWeight: 600 }}>Yes, dissolve</button>
                            <button onClick={() => setConfirmDel(null)} style={{ ...smallBtn, color: theme.color.muted }}>Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                    {expanded === g.id && (
                      <tr>
                        <td colSpan={2} style={{ ...cell, background: theme.color.panelAlt, padding: 0 }}>
                          {!members ? (
                            <div style={{ padding: "12px 16px", fontSize: 12, color: theme.color.subtle }}>Loading…</div>
                          ) : (
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                              <tbody>
                                {members.map((m) => (
                                  <tr key={m.id}>
                                    <td style={{ ...cell, borderBottom: `1px solid ${theme.color.border}` }}>
                                      <span style={{ fontWeight: 600 }}>{m.name}</span>
                                      {m.id === g.parentOrganizationId && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: theme.color.accentText }}>Parent</span>}
                                      <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>
                                        {m.frameworks.map((f) => (FRAMEWORK_LABELS as Record<string, string>)[f] ?? f).join(", ") || "No standards"}
                                      </div>
                                    </td>
                                    <td style={{ ...cell, fontSize: 12, whiteSpace: "nowrap" }}>
                                      {m.latestScore === null ? "No score" : `${m.latestScore}%`} · {m.openRisks} open risk{m.openRisks === 1 ? "" : "s"} · {m.userCount} member{m.userCount === 1 ? "" : "s"} · {m.siteCount} site{m.siteCount === 1 ? "" : "s"}
                                    </td>
                                    <td style={{ ...cell, textAlign: "right" }}>
                                      {onOpenClient && <button onClick={() => onOpenClient(m.id, m.name)} style={smallBtn}>Open</button>}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && <GroupModal clients={clients} grouped={grouped} onClose={() => setShowCreate(false)} onSaved={() => { setDetails(new Map()); load(); }} />}
      {editing && <GroupModal initial={editing} clients={clients} grouped={grouped} onClose={() => setEditing(null)} onSaved={() => { setDetails(new Map()); load(); }} />}
    </div>
  );
}
